import { useRef, useState } from 'react';
import { AlertDialog, AlertDialogContent } from '@/components/ui/alert-dialog';
import { Button } from '@/components/Button';
import DatePickerModal from '@/components/DatePickerModal';
import FormLabel from '@/components/auth/FormLabel';

type DateField = { key: string; label: string; defaultValue: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: (dates: Record<string, string>) => void | Promise<void>;
  isLoading?: boolean;
  dateFields?: DateField[];
  variant?: 'default' | 'danger';
};

export default function ProfitStatusModal({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  onConfirm,
  isLoading = false,
  dateFields = [],
  variant = 'default',
}: Props) {
  // モーダルは開くたびに再マウントされるため、初期化子で dateFields から state を組み立てれば十分
  // （useEffect による同期は不要）
  const [dates, setDates] = useState<Record<string, string>>(() =>
    Object.fromEntries(dateFields.map((field) => [field.key, field.defaultValue]))
  );

  // isLoading による disabled は再レンダーを待つため、連打すると 1 回目の描画前に
  // 2 回目のクリックが通ってしまう。レンダーに依存しない同期フラグで二重送信を防ぐ。
  const isSubmittingRef = useRef(false);

  const handleConfirm = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      await onConfirm(dates);
    } finally {
      // 失敗時はモーダルが開いたままなので、再試行できるようフラグを戻す
      isSubmittingRef.current = false;
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="block w-full max-w-screen-sm p-0">
        <div className="flex items-center border-b border-solid border-border px-lg py-md">
          <p className="text-base font-bold text-main">{title}</p>
        </div>
        <div className="flex flex-col gap-md p-lg">
          <p className="text-sm leading-[1.7] text-main">{description}</p>
          {dateFields.map((field) => (
            <div key={field.key} className="flex flex-col gap-xs">
              <FormLabel>{field.label}</FormLabel>
              <DatePickerModal
                value={dates[field.key]}
                onChange={(value) => setDates((prev) => ({ ...prev, [field.key]: value }))}
              />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-md border-t border-solid border-border px-lg py-md">
          <Button color="secondary" size="md" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
          <Button
            color={variant === 'danger' ? 'danger' : 'primary'}
            size="md"
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {isLoading ? '処理中...' : confirmLabel}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
